import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BarChart3, RefreshCw, Plus, Trash2, X, Loader2, Save, Download, Upload, Target, TrendingUp, SlidersHorizontal, Wallet, Film, Gauge, ListChecks, Database, ChevronDown, ChevronUp, ChevronRight, Youtube, CalendarDays, FileSpreadsheet } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ComposedChart, AreaChart, Area } from "recharts";
import { API, authHeaders } from "@/constants/roles";
import { fmtMoney, fmtNum } from "@/utils/currency";

const CYAN = "#06b6d4";
const ROSE = "#f43f5e";
const EMERALD = "#10b981";

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";
const labelCls = "mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]";
const cardCls = "rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6";
// Display-currency aware (owner preference; every value is stored in USD and converted for display only)
const money = fmtMoney;
const num = fmtNum;
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const tooltipStyle = { backgroundColor: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", color: "#e2e8f0" };

const TABS = [
  { id: "overview", label: "Overview", icon: Gauge },
  { id: "channel", label: "Channel", icon: TrendingUp },
  { id: "money", label: "Money", icon: Wallet },
  { id: "production", label: "Production", icon: BarChart3 },
  { id: "videos", label: "Videos", icon: Film },
  { id: "trackers", label: "Trackers & Goals", icon: ListChecks },
  { id: "data", label: "Data", icon: Database },
];

export const AdminAnalytics = () => {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [widgets, setWidgets] = useState(null);
  const [trackers, setTrackers] = useState([]);
  const [expenses, setExpenses] = useState({ items: [], recurring: [] });
  const [dateFrom, setDateFrom] = useState(daysAgo(29));
  const [dateTo, setDateTo] = useState(today());
  const navigate = useNavigate();

  const guard = (err) => {
    if (err.response?.status === 401 || err.response?.status === 403) navigate("/login");
    else toast.error("Could not load analytics.");
  };

  const loadSummary = useCallback(() => {
    axios.get(`${API}/admin/analytics/summary?date_from=${dateFrom}&date_to=${dateTo}`, { headers: authHeaders() })
      .then((r) => setSummary(r.data)).catch(guard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);
  const loadWidgets = useCallback(() => {
    axios.get(`${API}/admin/analytics/widgets`, { headers: authHeaders() }).then((r) => setWidgets(r.data.widgets)).catch(() => {});
  }, []);
  const loadTrackers = useCallback(() => {
    axios.get(`${API}/admin/analytics/trackers`, { headers: authHeaders() }).then((r) => setTrackers(r.data)).catch(() => {});
  }, []);
  const loadExpenses = useCallback(() => {
    axios.get(`${API}/admin/analytics/expenses`, { headers: authHeaders() }).then((r) => setExpenses(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadWidgets(); loadTrackers(); loadExpenses(); }, [loadWidgets, loadTrackers, loadExpenses]);

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Analytics</h1>
        <p className="mt-1 text-sm text-[var(--pv-text-4)]">The whole business in one place — channel, money, production, videos.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="analytics-date-from" type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-2)] focus:border-cyan-400 focus:outline-none" />
        <span className="text-xs text-[var(--pv-text-4)]">to</span>
        <input data-testid="analytics-date-to" type="date" value={dateTo} min={dateFrom} max={today()} onChange={(e) => setDateTo(e.target.value)} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-2)] focus:border-cyan-400 focus:outline-none" />
        <button data-testid="analytics-refresh-btn" onClick={() => { loadSummary(); loadTrackers(); loadExpenses(); }} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-4)] transition-colors hover:bg-[var(--pv-subtle)]">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
    </div>
  );

  const tabBar = (
    <div data-testid="analytics-tabs" className="mt-5 flex gap-2 overflow-x-auto pb-1">
      {TABS.map((t) => (
        <button key={t.id} data-testid={`analytics-tab-${t.id}`} onClick={() => setTab(t.id)}
          className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${tab === t.id ? "bg-cyan-500 text-white shadow-md shadow-cyan-500/25" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-3)] hover:border-cyan-500/30"}`}>
          <t.icon className="h-3.5 w-3.5" /> {t.label}
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="admin-analytics">
      {header}
      {tabBar}
      {!summary ? (
        <p data-testid="analytics-loading" className="py-20 text-center text-sm font-medium text-[var(--pv-text-4)]"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : (
        <div className="mt-6">
          {tab === "overview" && <OverviewTab summary={summary} widgets={widgets} onSaveWidgets={(w) => { setWidgets(w); axios.put(`${API}/admin/analytics/widgets`, { widgets: w }, { headers: authHeaders() }).then(() => toast.success("Layout saved.")).catch(() => toast.error("Could not save layout.")); }} />}
          {tab === "channel" && <ChannelTab summary={summary} onChanged={loadSummary} onRange={(f, t) => { setDateFrom(f); setDateTo(t); }} />}
          {tab === "money" && <MoneyTab summary={summary} expenses={expenses} onChanged={() => { loadExpenses(); loadSummary(); }} />}
          {tab === "production" && <ProductionTab summary={summary} />}
          {tab === "videos" && <VideosTab summary={summary} />}
          {tab === "trackers" && <TrackersTab summary={summary} trackers={trackers} onChanged={() => { loadTrackers(); loadSummary(); }} />}
          {tab === "data" && <DataTab onChanged={loadSummary} />}
        </div>
      )}
    </div>
  );
};

/* ==================== OVERVIEW (customizable widgets) ==================== */
const StatWidget = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className={`${cardCls} h-full`}>
    <p className="font-display text-3xl font-bold tracking-tight text-[var(--pv-text)]">{value}</p>
    <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{label}</p>
    {sub && <p className="mt-1 text-xs text-[var(--pv-text-4)]">{sub}</p>}
  </div>
);

const OverviewTab = ({ summary, widgets, onSaveWidgets }) => {
  const [customizing, setCustomizing] = useState(false);
  const [draft, setDraft] = useState(null);
  const { channel, finance, production, calculated, daily } = summary;

  const chartData = daily.map((d) => ({ date: d.date.slice(5), revenue: d.revenue, views: d.views }));

  const CATALOG = {
    revenue_month: { label: `Revenue (${summary.range.days}d)`, render: () => <StatWidget testid="wg-revenue" label={`Revenue · ${summary.range.days}d`} value={money(channel.revenue)} sub={`${money(calculated.revenue_per_1m_views ?? 0)} per 1M views`} /> },
    views_month: { label: `Views (${summary.range.days}d)`, render: () => <StatWidget testid="wg-views" label={`Views · ${summary.range.days}d`} value={num(channel.views)} sub={`+${num(channel.subs_gained)} subs`} /> },
    profit_month: { label: "Profit", render: () => <StatWidget testid="wg-profit" label={`Profit · ${summary.range.days}d`} value={money(finance.profit)} sub="revenue + income − expenses − production" /> },
    production_spend_month: { label: "Production spend", render: () => <StatWidget testid="wg-spend" label={`Production paid · ${summary.range.days}d`} value={money(finance.production_paid)} sub={`${money(calculated.production_spend_per_week)} / week`} /> },
    amount_owed: { label: "Amount owed", render: () => <StatWidget testid="wg-owed" label="Owed to workers" value={money(finance.amount_owed)} sub="queued payments" /> },
    committed_budget: { label: "Committed budget", render: () => <StatWidget testid="wg-committed" label="Committed (active jobs)" value={money(finance.committed)} sub={`${money(finance.available_budget)} still available`} /> },
    active_jobs: { label: "Active jobs", render: () => <StatWidget testid="wg-active" label="Active jobs" value={production.active_jobs} sub={`${production.in_revision} in revision`} /> },
    awaiting_review: { label: "Awaiting review", render: () => <StatWidget testid="wg-review" label="Awaiting review" value={production.awaiting_review} sub="submissions in your queue" /> },
    videos_completed: { label: "Jobs completed", render: () => <StatWidget testid="wg-completed" label={`Jobs completed · ${summary.range.days}d`} value={production.jobs_completed} sub={`${calculated.videos_per_week} videos / week pace`} /> },
    expenses: { label: "Expenses", render: () => <StatWidget testid="wg-expenses" label={`Expenses · ${summary.range.days}d`} value={money(finance.expenses)} sub="incl. recurring" /> },
    business_income: { label: "Other income", render: () => <StatWidget testid="wg-income" label={`Business income · ${summary.range.days}d`} value={money(finance.business_income)} sub="sponsors, etc." /> },
    avg_turnaround: { label: "Avg turnaround", render: () => <StatWidget testid="wg-turnaround" label="Avg turnaround" value={calculated.avg_turnaround_hours != null ? `${calculated.avg_turnaround_hours}h` : "—"} sub="claim → completion" /> },
    daily_revenue_chart: {
      label: "Daily revenue chart",
      render: () => (
        <div data-testid="wg-revenue-chart" className={`${cardCls} h-full`}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Daily revenue</p>
          <div className="mt-3" style={{ width: "100%", height: 220 }}>
            {chartData.length === 0 ? <p className="flex h-full items-center justify-center text-sm text-[var(--pv-text-4)]">Add daily metrics in the Channel tab.</p> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid stroke="rgba(148,163,184,0.12)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} stroke="#334155" />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} stroke="#334155" width={50} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line type="monotone" dataKey="revenue" stroke={CYAN} strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      ),
    },
    daily_views_chart: {
      label: "Daily views chart",
      render: () => (
        <div data-testid="wg-views-chart" className={`${cardCls} h-full`}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Daily views</p>
          <div className="mt-3" style={{ width: "100%", height: 220 }}>
            {chartData.length === 0 ? <p className="flex h-full items-center justify-center text-sm text-[var(--pv-text-4)]">Add daily metrics in the Channel tab.</p> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid stroke="rgba(148,163,184,0.12)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} stroke="#334155" />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} stroke="#334155" width={60} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="views" fill={CYAN} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      ),
    },
  };

  const active = widgets || [];
  const sizeCls = { small: "", medium: "sm:col-span-2", large: "sm:col-span-2 lg:col-span-3" };

  const startCustomize = () => { setDraft(active.map((w) => ({ ...w }))); setCustomizing(true); };
  const toggleWidget = (id) => setDraft((d) => (d.some((w) => w.id === id) ? d.filter((w) => w.id !== id) : [...d, { id, size: "small" }]));
  const cycleSize = (id) => setDraft((d) => d.map((w) => (w.id === id ? { ...w, size: { small: "medium", medium: "large", large: "small" }[w.size] } : w)));

  return (
    <div data-testid="analytics-overview">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--pv-text-4)]">Your command center — pick the numbers you watch every day.</p>
        <button data-testid="widgets-customize-btn" onClick={customizing ? () => setCustomizing(false) : startCustomize} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-2 text-xs font-semibold text-[var(--pv-text-3)] hover:border-cyan-500/30">
          <SlidersHorizontal className="h-3.5 w-3.5" /> {customizing ? "Cancel" : "Customize"}
        </button>
      </div>

      {customizing && draft && (
        <div data-testid="widgets-customizer" className="mt-4 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Choose widgets</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(CATALOG).map(([id, def]) => {
              const on = draft.some((w) => w.id === id);
              const w = draft.find((x) => x.id === id);
              return (
                <span key={id} className={`flex items-center gap-1 rounded-full border px-1 py-1 text-xs font-semibold ${on ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300" : "border-[var(--pv-border)] text-[var(--pv-text-4)]"}`}>
                  <button data-testid={`widget-toggle-${id}`} onClick={() => toggleWidget(id)} className="px-2">{def.label}</button>
                  {on && <button data-testid={`widget-size-${id}`} onClick={() => cycleSize(id)} className="rounded-full bg-cyan-500/20 px-2 py-0.5 text-[10px] font-bold uppercase">{w.size[0]}</button>}
                </span>
              );
            })}
          </div>
          <button data-testid="widgets-save-btn" onClick={() => { onSaveWidgets(draft); setCustomizing(false); }} className="mt-4 flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600">
            <Save className="h-3.5 w-3.5" /> Save layout
          </button>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {active.map((w) => CATALOG[w.id] ? <div key={w.id} className={sizeCls[w.size] || ""}>{CATALOG[w.id].render()}</div> : null)}
        {active.length === 0 && <p className="col-span-full rounded-3xl border border-dashed border-[var(--pv-border-strong)] py-14 text-center text-sm text-[var(--pv-text-4)]">No widgets selected — hit Customize to build your board.</p>}
      </div>

      {summary.goals.length > 0 && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {summary.goals.map((g) => (
            <div key={g.id} data-testid={`goal-card-${g.id}`} className={cardCls}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-[var(--pv-text)]">{g.name}</p>
                <Target className="h-4 w-4 text-cyan-400" />
              </div>
              <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[var(--pv-subtle)]">
                <div className={`h-full rounded-full ${g.pct >= 100 ? "bg-emerald-400" : "bg-cyan-500"}`} style={{ width: `${g.pct}%` }} />
              </div>
              <p className="mt-2 text-xs text-[var(--pv-text-4)]">
                <span className="font-bold text-[var(--pv-text)]">{num(g.current)}</span> / {num(g.target)} {g.metric.replace(/_/g, " ")} · {g.pct}% · {g.days_left}d left ({g.period})
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ==================== CHANNEL (daily metrics) ==================== */
const CHART_METRICS = [
  { id: "revenue", label: "Revenue", color: "#10b981", fmt: (v) => money(v) },
  { id: "views", label: "Views", color: "#22d3ee", fmt: fmtNum },
  { id: "engaged_views", label: "Engaged views", color: "#a78bfa", fmt: fmtNum },
  { id: "watch_minutes", label: "Watch time", color: "#f59e0b", fmt: (v) => `${Math.round((v || 0) / 60).toLocaleString()}h`, map: (d) => (d.watch_minutes || 0) / 60 },
  { id: "subs_net", label: "Net subscribers", color: "#34d399", fmt: (v) => `${v > 0 ? "+" : ""}${fmtNum(v)}`, map: (d) => (d.subs_gained || 0) - (d.subs_lost || 0) },
];

const addDays = (isoDate, delta) => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};

const emptyDay = () => ({ date: today(), views: "", revenue: "", videos_published: "", subs_gained: "", business_income: "", notes: "" });

// Shared manual-fallback pieces (used when YouTube is not connected, or via the collapsed section)
const CsvImportCard = ({ importing, dragOver, setDragOver, importFile, fileRef }) => (
  <div className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-5">
    <h3 className="mb-1 font-display text-sm font-bold text-[var(--pv-text)]">Import CSV</h3>
    <p className="mb-3 text-[10px] text-[var(--pv-text-4)]">
      Columns: date, views, revenue, videos_published, subs_gained, business_income, notes
      <a data-testid="metrics-template-btn" href={`${API}/admin/metrics/template`} className="ml-1.5 inline-flex items-center gap-0.5 font-semibold text-cyan-400 hover:text-cyan-300"><Download className="h-3 w-3" />template</a>
    </p>
    <button type="button" data-testid="metrics-dropzone"
      onClick={() => fileRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); importFile(e.dataTransfer.files?.[0]); }}
      className={`flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed p-5 transition-colors ${dragOver ? "border-cyan-400 bg-cyan-500/10" : "border-[var(--pv-border-strong)] bg-[var(--pv-bg)] hover:border-cyan-500/40"}`}>
      {importing ? <Loader2 className="h-6 w-6 animate-spin text-cyan-400" /> : <FileSpreadsheet className="h-6 w-6 text-[var(--pv-text-4)]" />}
      <span className="text-[11px] font-semibold text-[var(--pv-text-3)]">{importing ? "Importing…" : "Drop CSV or click to browse"}</span>
    </button>
    <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="metrics-file-input" onChange={(e) => importFile(e.target.files?.[0])} />
  </div>
);

const DailyTable = ({ summary, onDelete }) => (
  <div className="overflow-hidden rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)]">
    <div className="max-h-[520px] overflow-y-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-[var(--pv-subtle)]">
          <tr className="border-b border-[var(--pv-border)]">
            {["Date", "Views", "Revenue", "Biz income", "Videos", "Subs", "Notes", ""].map((h) => <th key={h} className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {[...summary.daily].reverse().map((d) => (
            <tr key={d.date} data-testid={`metric-row-${d.date}`} className="border-b border-[var(--pv-border)] last:border-0 hover:bg-[var(--pv-subtle)]">
              <td className="px-3 py-2 font-medium text-[var(--pv-text)]">
                {d.date}
                {d.source === "youtube" && <span className="ml-1.5 rounded bg-rose-500/15 px-1 py-0.5 text-[8px] font-bold text-rose-400" title="Synced from YouTube">YT</span>}
              </td>
              <td className="px-3 py-2 text-[var(--pv-text-3)]">{fmtNum(d.views)}</td>
              <td className="px-3 py-2 text-emerald-400">{money(d.revenue)}</td>
              <td className="px-3 py-2 text-amber-400">{money(d.business_income)}</td>
              <td className="px-3 py-2 text-[var(--pv-text-3)]">{d.videos_published || "—"}</td>
              <td className="px-3 py-2 text-[var(--pv-text-3)]">{d.subs_gained ? `+${d.subs_gained}` : "—"}</td>
              <td className="max-w-28 truncate px-3 py-2 text-[var(--pv-text-4)]" title={d.notes}>{d.notes || "—"}</td>
              <td className="px-3 py-2">
                {d.source !== "youtube" && (
                  <button data-testid={`metric-delete-${d.date}`} onClick={() => onDelete(d.date)} aria-label={`Delete ${d.date}`} className="rounded-lg p-1.5 text-[var(--pv-text-4)] transition-colors hover:bg-rose-500/10 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {summary.daily.length === 0 && (
      <div className="py-16 text-center">
        <CalendarDays className="mx-auto h-8 w-8 text-[var(--pv-text-4)]" />
        <p data-testid="metrics-empty" className="mt-2 text-xs text-[var(--pv-text-4)]">No data in this range yet.</p>
      </div>
    )}
  </div>
);

const ChannelTab = ({ summary, onChanged, onRange }) => {
  const yt = summary.youtube;
  const [form, setForm] = useState(emptyDay());
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [metric, setMetric] = useState("revenue");
  const [lifetime, setLifetime] = useState(null);
  const [published, setPublished] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!yt?.connected) return;
    axios.get(`${API}/youtube/channel/daily`, { headers: authHeaders() })
      .then((r) => { setLifetime(r.data.lifetime || null); setPublished(r.data.published || []); })
      .catch(() => {});
  }, [yt?.connected, yt?.last_sync]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/metrics`, {
        date: form.date,
        views: parseFloat(form.views) || 0,
        revenue: parseFloat(form.revenue) || 0,
        videos_published: parseInt(form.videos_published) || 0,
        subs_gained: parseInt(form.subs_gained) || 0,
        business_income: parseFloat(form.business_income) || 0,
        notes: form.notes || "",
      }, { headers: authHeaders() });
      toast.success("Saved.");
      setForm(emptyDay());
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/metrics/import-csv`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.imported} day(s)`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} row(s) skipped`);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteDay = async (date) => {
    try {
      await axios.delete(`${API}/admin/metrics/${date}`, { headers: authHeaders() });
      toast.success("Deleted");
      onChanged();
    } catch { toast.error("Failed"); }
  };

  const manualBlocks = (
    <>
      <form onSubmit={submit} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-5">
        <h3 className="mb-3 font-display text-sm font-bold text-[var(--pv-text)]">Log daily metrics</h3>
        <div className="space-y-2.5">
          <input data-testid="metric-date" type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="w-full rounded-xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3 py-2 text-xs text-[var(--pv-text)] [color-scheme:dark]" />
          <div className="grid grid-cols-2 gap-2">
            {[["views", "Views"], ["revenue", "Revenue $"], ["videos_published", "Videos out"], ["subs_gained", "Subs gained"], ["business_income", "Business income $"]].map(([k, ph]) => (
              <input key={k} data-testid={`metric-${k}`} type="number" step="any" min="0" placeholder={ph} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                className="w-full rounded-xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3 py-2 text-xs text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)]" />
            ))}
          </div>
          <input data-testid="metric-notes" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="w-full rounded-xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3 py-2 text-xs text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)]" />
        </div>
        <button data-testid="metric-save-btn" type="submit" disabled={saving} className="mt-3 flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Save day
        </button>
      </form>
      <CsvImportCard importing={importing} dragOver={dragOver} setDragOver={setDragOver} importFile={importFile} fileRef={fileRef} />
    </>
  );

  // ---------- Not connected: manual fallback UI ----------
  if (!yt?.connected) {
    return (
      <div data-testid="analytics-channel" className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <div className="space-y-5">
          <div className="rounded-2xl border border-rose-500/25 bg-rose-500/5 p-4">
            <p className="flex items-center gap-2 text-xs font-bold text-rose-300"><Youtube className="h-4 w-4" /> YouTube not connected</p>
            <p className="mt-1 text-[11px] text-[var(--pv-text-4)]">Connect the channel in Videos to auto-sync daily views, revenue, watch time and subscribers — no manual logging needed. Manual entry stays available as a fallback.</p>
          </div>
          {manualBlocks}
        </div>
        <DailyTable summary={summary} onDelete={deleteDay} />
      </div>
    );
  }

  // ---------- Connected: YouTube Studio-style channel dashboard ----------
  const cov = yt.coverage || {};
  const end = cov.last || new Date().toISOString().slice(0, 10);
  const applyPreset = (id) => {
    let from;
    if (id === "7D") from = addDays(end, -6);
    else if (id === "28D") from = addDays(end, -27);
    else if (id === "90D") from = addDays(end, -89);
    else if (id === "YTD") from = `${end.slice(0, 4)}-01-01`;
    else if (id === "Lifetime") from = cov.first || end;
    else return; // Custom: user edits the date inputs above
    onRange(from, end);
  };
  const m = CHART_METRICS.find((x) => x.id === metric);
  const chartData = summary.daily.map((d) => ({ ...d, _v: m.map ? m.map(d) : (d[metric] ?? 0) }));
  const rangePublished = published.filter((p) => p.date >= summary.range.from && p.date <= summary.range.to);
  const cards = [
    { label: "Views", value: fmtNum(yt.views), testid: "ytc-views" },
    { label: "Engaged views", value: fmtNum(yt.engaged_views), testid: "ytc-engaged" },
    { label: "Watch time (hrs)", value: Math.round((yt.watch_minutes || 0) / 60).toLocaleString(), testid: "ytc-watch" },
    { label: "Est. revenue", value: money(yt.revenue), testid: "ytc-revenue", color: "text-emerald-400" },
    { label: "Subs gained", value: `+${fmtNum(yt.subs_gained)}`, testid: "ytc-subs-gained" },
    { label: "Subs lost", value: `-${fmtNum(yt.subs_lost)}`, testid: "ytc-subs-lost" },
    { label: "Net subscribers", value: `${yt.subs_net >= 0 ? "+" : ""}${fmtNum(yt.subs_net)}`, testid: "ytc-subs-net", color: yt.subs_net >= 0 ? "text-emerald-400" : "text-rose-400" },
    { label: "Videos published", value: yt.videos_published, testid: "ytc-published" },
  ];

  return (
    <div data-testid="analytics-channel" className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="ytc-synced-badge" className="flex items-center gap-1.5 rounded-full border border-rose-500/25 bg-rose-500/10 px-3 py-1.5 text-[11px] font-bold text-rose-300">
          <Youtube className="h-3.5 w-3.5" /> {yt.channel_title} — auto-synced from YouTube{yt.last_sync && <> · {new Date(yt.last_sync).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</>}
        </span>
        {cov.first && <span className="text-[10px] text-[var(--pv-text-4)]">coverage {cov.first} → {cov.last} (USD)</span>}
        <div className="ml-auto flex flex-wrap gap-1" data-testid="ytc-presets">
          {["7D", "28D", "90D", "YTD", "Lifetime", "Custom"].map((p) => (
            <button key={p} data-testid={`ytc-preset-${p}`} onClick={() => applyPreset(p)}
              className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3 py-1 text-[11px] font-bold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/40 hover:text-cyan-400">
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">{c.label}</p>
            <p className={`mt-1 font-display text-lg font-bold ${c.color || "text-[var(--pv-text)]"}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4 sm:p-5">
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {CHART_METRICS.map((cm) => (
            <button key={cm.id} data-testid={`ytc-metric-${cm.id}`} onClick={() => setMetric(cm.id)}
              className={`rounded-full px-3 py-1.5 text-[11px] font-bold transition-colors ${metric === cm.id ? "text-white" : "bg-[var(--pv-subtle)] text-[var(--pv-text-3)] hover:text-[var(--pv-text)]"}`}
              style={metric === cm.id ? { backgroundColor: cm.color } : undefined}>
              {cm.label}
            </button>
          ))}
          <span className="ml-auto text-[10px] text-[var(--pv-text-4)]">{summary.range.from} → {summary.range.to} · hover for exact daily values</span>
        </div>
        {chartData.length === 0 ? (
          <p className="py-12 text-center text-xs text-[var(--pv-text-4)]">No synced data in this range — press Sync now in Videos.</p>
        ) : (
          <div data-testid="ytc-chart" className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="ytcGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={m.color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={m.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--pv-border)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--pv-text-4)" }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 9, fill: "var(--pv-text-4)" }} tickFormatter={(v) => (m.map ? m.fmt(v) : fmtNum(v))} width={88} />
                <Tooltip
                  contentStyle={{ background: "var(--pv-card)", border: "1px solid var(--pv-border)", borderRadius: 12, fontSize: 11 }}
                  formatter={(v) => [m.fmt(v), m.label]}
                />
                <Area type="monotone" dataKey="_v" stroke={m.color} strokeWidth={2} fill="url(#ytcGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        {rangePublished.length > 0 && (
          <div className="mt-3 border-t border-[var(--pv-border)] pt-3" data-testid="ytc-published-list">
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Published in this range ({rangePublished.length})</p>
            <div className="flex flex-wrap gap-1.5">
              {rangePublished.map((p) => (
                <span key={p.youtube_id} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-2.5 py-1 text-[10px] font-semibold text-[var(--pv-text-3)]" title={p.title}>
                  {p.date.slice(5)} · {p.title?.length > 30 ? `${p.title.slice(0, 30)}…` : p.title}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {lifetime && (
        <div data-testid="ytc-lifetime" className="rounded-2xl border border-amber-500/25 bg-amber-500/5 p-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400/80">Channel totals (live from YouTube)</p>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              { label: "Channel views (lifetime)", value: fmtNum(lifetime.views), testid: "ytc-life-views" },
              { label: "Subscribers (current)", value: fmtNum(lifetime.subscribers), testid: "ytc-life-subs" },
              { label: "Videos (current)", value: lifetime.video_count, testid: "ytc-life-videos" },
              { label: "Revenue (analytics period)", value: money(lifetime.revenue), testid: "ytc-life-revenue" },
              { label: "Watch hours (analytics period)", value: Math.round((lifetime.watch_minutes || 0) / 60).toLocaleString(), testid: "ytc-life-watch" },
            ].map((c) => (
              <div key={c.testid} data-testid={c.testid}>
                <p className="font-display text-lg font-bold text-[var(--pv-text)]">{c.value}</p>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{c.label}</p>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-[var(--pv-text-4)]">Analytics-period totals cover {lifetime.revenue_since} onward (API coverage), USD. Subscriber/view counters are YouTube's current lifetime values.</p>
        </div>
      )}

      <div className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)]">
        <button data-testid="ytc-manual-toggle" onClick={() => setShowManual((s) => !s)} className="flex w-full items-center gap-2 px-5 py-3.5 text-left">
          {showManual ? <ChevronDown className="h-3.5 w-3.5 text-[var(--pv-text-4)]" /> : <ChevronRight className="h-3.5 w-3.5 text-[var(--pv-text-4)]" />}
          <span className="font-display text-sm font-bold text-[var(--pv-text)]">Manual / non-YouTube metrics</span>
          <span className="text-[10px] text-[var(--pv-text-4)]">optional fallback — channel metrics sync automatically</span>
        </button>
        {showManual && (
          <div className="grid gap-5 border-t border-[var(--pv-border)] p-5 lg:grid-cols-[380px_1fr]">
            <div className="space-y-5">{manualBlocks}</div>
            <DailyTable summary={summary} onDelete={deleteDay} />
          </div>
        )}
      </div>
    </div>
  );
};
const MoneyTab = ({ summary, expenses, onChanged }) => {
  const [form, setForm] = useState({ date: today(), amount: "", category: "", notes: "", recurring: false });
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const f = summary.finance;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/analytics/expenses`, {
        date: form.date, amount: parseFloat(form.amount) || 0, category: form.category.trim(),
        notes: form.notes, recurring: form.recurring ? "monthly" : null,
      }, { headers: authHeaders() });
      toast.success("Expense logged.");
      setShowForm(false);
      setForm({ date: today(), amount: "", category: "", notes: "", recurring: false });
      onChanged();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save expense.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/analytics/expenses/${id}`, { headers: authHeaders() });
      toast.success("Expense deleted.");
      onChanged();
    } catch { toast.error("Could not delete expense."); }
  };

  const cards = [
    { label: summary.youtube?.connected ? `YouTube revenue · ${summary.range.days}d` : `Revenue · ${summary.range.days}d`, value: money(f.revenue), testid: "mn-revenue", sub: summary.youtube?.connected ? "auto-synced from YouTube (USD)" : undefined },
    { label: "Other business income", value: money(f.business_income), testid: "mn-income" },
    { label: "Expenses", value: money(f.expenses), testid: "mn-expenses" },
    { label: "Production paid", value: money(f.production_paid), testid: "mn-paid" },
    { label: "Profit", value: money(f.profit), testid: "mn-profit", accent: f.profit >= 0 ? "text-emerald-400" : "text-rose-400" },
    { label: "Owed to workers", value: money(f.amount_owed), testid: "mn-owed" },
    { label: "Committed (active jobs)", value: money(f.committed), testid: "mn-committed" },
    { label: "Budget available", value: money(f.available_budget), testid: "mn-available", sub: `of ${money(f.total_budget)} total` },
  ];

  return (
    <div data-testid="analytics-money" className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className={cardCls}>
            <p className={`font-display text-2xl font-bold tracking-tight ${c.accent || "text-[var(--pv-text)]"}`}>{c.value}</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{c.label}</p>
            {c.sub && <p className="mt-0.5 text-xs text-[var(--pv-text-4)]">{c.sub}</p>}
          </div>
        ))}
      </div>

      <div className={cardCls}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Business expenses</p>
            <p className="mt-1 text-xs text-[var(--pv-text-4)]">Software, assets, subscriptions — recurring monthly expenses count once per month in every range.</p>
          </div>
          <button data-testid="expense-add-btn" onClick={() => setShowForm((s) => !s)} className="flex shrink-0 items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600">
            {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Close" : "Add expense"}
          </button>
        </div>

        {showForm && (
          <form data-testid="expense-form" onSubmit={save} className="mt-4 grid gap-3 rounded-2xl border border-cyan-500/20 bg-[var(--pv-subtle)] p-4 sm:grid-cols-2 lg:grid-cols-5">
            <div><label className={labelCls}>Date</label><input data-testid="expense-date" required type="date" value={form.date} onChange={(e) => setForm((x) => ({ ...x, date: e.target.value }))} className={inputCls} /></div>
            <div><label className={labelCls}>Amount (USD)</label><input data-testid="expense-amount" required type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm((x) => ({ ...x, amount: e.target.value }))} className={inputCls} /></div>
            <div><label className={labelCls}>Category</label><input data-testid="expense-category" required value={form.category} onChange={(e) => setForm((x) => ({ ...x, category: e.target.value }))} className={inputCls} placeholder="Software" /></div>
            <div><label className={labelCls}>Notes</label><input data-testid="expense-notes" value={form.notes} onChange={(e) => setForm((x) => ({ ...x, notes: e.target.value }))} className={inputCls} placeholder="optional" /></div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-xs font-semibold text-[var(--pv-text-3)]">
                <input data-testid="expense-recurring" type="checkbox" checked={form.recurring} onChange={(e) => setForm((x) => ({ ...x, recurring: e.target.checked }))} className="h-4 w-4 accent-cyan-500" /> Monthly
              </label>
              <button data-testid="expense-save-btn" type="submit" disabled={saving} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
            </div>
          </form>
        )}

        {expenses.items.length === 0 ? (
          <p data-testid="expenses-empty" className="mt-4 text-sm text-[var(--pv-text-4)]">No expenses logged yet.</p>
        ) : (
          <div data-testid="expenses-table" className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-xs">
              <thead>
                <tr className="border-b border-[var(--pv-border)] text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
                  <th className="py-2 pr-4">Date</th><th className="px-4 py-2">Category</th><th className="px-4 py-2">Amount</th><th className="px-4 py-2">Recurs</th><th className="px-4 py-2">Notes</th><th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.items.map((e) => (
                  <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-[var(--pv-border)] last:border-0">
                    <td className="py-2 pr-4 font-bold text-[var(--pv-text)]">{e.date}</td>
                    <td className="px-4 py-2 text-[var(--pv-text-3)]">{e.category}</td>
                    <td className="px-4 py-2 text-[var(--pv-text-2)]">{money(e.amount)}</td>
                    <td className="px-4 py-2">{e.recurring === "monthly" ? <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold text-cyan-300">monthly</span> : <span className="text-[var(--pv-text-4)]">—</span>}</td>
                    <td className="max-w-[180px] truncate px-4 py-2 text-[var(--pv-text-4)]">{e.notes || "—"}</td>
                    <td className="py-2 text-right"><button data-testid={`expense-delete-${e.id}`} onClick={() => remove(e.id)} className="rounded-full p-1.5 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/* ==================== PRODUCTION ==================== */
const ProductionTab = ({ summary }) => {
  const p = summary.production;
  const c = summary.calculated;
  const workerData = Object.entries(summary.per_worker).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  const cards = [
    { label: `Jobs created · ${summary.range.days}d`, value: p.jobs_created, testid: "pr-created" },
    { label: `Jobs completed · ${summary.range.days}d`, value: p.jobs_completed, testid: "pr-completed" },
    { label: "Active right now", value: p.active_jobs, testid: "pr-active" },
    { label: "Awaiting review", value: p.awaiting_review, testid: "pr-review" },
    { label: "In revision", value: p.in_revision, testid: "pr-revision" },
    { label: `Revisions · ${summary.range.days}d`, value: p.revisions, testid: "pr-revisions" },
    { label: "Avg turnaround", value: c.avg_turnaround_hours != null ? `${c.avg_turnaround_hours}h` : "—", testid: "pr-turnaround" },
    { label: "Avg revisions / video", value: c.avg_revisions_per_video ?? "—", testid: "pr-rev-per-video" },
  ];
  return (
    <div data-testid="analytics-production" className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((x) => (
          <div key={x.testid} data-testid={x.testid} className={cardCls}>
            <p className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">{x.value}</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{x.label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className={cardCls}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Output pace</p>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {[{ l: "per day", v: c.videos_per_day }, { l: "per week", v: c.videos_per_week }, { l: "per month", v: c.videos_per_month }].map((x) => (
              <div key={x.l} className="rounded-2xl bg-[var(--pv-subtle)] p-4 text-center">
                <p className="font-display text-2xl font-bold text-[var(--pv-text)]">{x.v}</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">videos {x.l}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {[{ l: "per day", v: money(c.production_spend_per_day) }, { l: "per week", v: money(c.production_spend_per_week) }, { l: "per month", v: money(c.production_spend_per_month) }].map((x) => (
              <div key={x.l} className="rounded-2xl bg-[var(--pv-subtle)] p-4 text-center">
                <p className="font-display text-lg font-bold text-[var(--pv-text)]">{x.v}</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">spend {x.l}</p>
              </div>
            ))}
          </div>
        </div>

        <div className={cardCls}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Completions per worker (in range)</p>
          <div data-testid="production-worker-chart" className="mt-4" style={{ width: "100%", height: 220 }}>
            {workerData.length === 0 ? (
              <p className="flex h-full items-center justify-center text-sm text-[var(--pv-text-4)]">No completions in this range.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={workerData} layout="vertical">
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} stroke="#334155" />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#94a3b8" }} stroke="#334155" />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill={CYAN} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==================== VIDEOS ==================== */
const VideosTab = ({ summary }) => {
  const [sortKey, setSortKey] = useState("views");
  const [asc, setAsc] = useState(false);
  const rows = useMemo(() => {
    const r = [...summary.per_video];
    r.sort((a, b) => ((a[sortKey] ?? -Infinity) - (b[sortKey] ?? -Infinity)) * (asc ? 1 : -1));
    return r;
  }, [summary.per_video, sortKey, asc]);

  const headerCell = (key, label) => (
    <th key={key} className="cursor-pointer px-4 py-3 select-none" onClick={() => { setSortKey(key); setAsc(sortKey === key ? !asc : false); }}>
      <span className="inline-flex items-center gap-1">{label}{sortKey === key ? (asc ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : null}</span>
    </th>
  );

  const c = summary.calculated;
  return (
    <div data-testid="analytics-videos" className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatWidget testid="vp-avg-views" label="Avg views / video" value={c.avg_views_per_video != null ? num(c.avg_views_per_video) : "—"} />
        <StatWidget testid="vp-avg-cost" label="Avg cost / video" value={c.avg_production_cost_per_video != null ? money(c.avg_production_cost_per_video) : "—"} />
        <StatWidget testid="vp-rev-video" label="Revenue / video" value={c.revenue_per_video != null ? money(c.revenue_per_video) : "—"} />
        <StatWidget testid="vp-profit-video" label="Profit / video" value={c.profit_per_video != null ? money(c.profit_per_video) : "—"} />
      </div>

      <div className={`${cardCls} overflow-x-auto p-0`}>
        {rows.length === 0 ? (
          <p data-testid="vp-empty" className="py-16 text-center text-sm text-[var(--pv-text-4)]">No videos yet — approved jobs appear here automatically.</p>
        ) : (
          <table data-testid="vp-table" className="w-full min-w-[900px] text-left text-xs">
            <thead>
              <tr className="border-b border-[var(--pv-border)] text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
                <th className="px-5 py-3">Video</th>
                {headerCell("views", "Views")}
                {headerCell("revenue", "Revenue")}
                {headerCell("cost", "Cost")}
                {headerCell("profit", "Profit")}
                {headerCell("revisions", "Revisions")}
                <th className="px-4 py-3">Crew</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id} data-testid={`vp-row-${v.id}`} className="border-b border-[var(--pv-border)] last:border-0">
                  <td className="max-w-[220px] truncate px-5 py-3 font-bold text-[var(--pv-text)]">
                    {v.title}
                    {v.source === "youtube" && <span className="ml-1.5 rounded bg-rose-500/15 px-1 py-0.5 text-[8px] font-bold text-rose-400" title="Channel-owned YouTube video (auto-synced, no production crew)">YT</span>}
                    {v.source === "youtube+internal" && <span className="ml-1.5 rounded bg-emerald-500/15 px-1 py-0.5 text-[8px] font-bold text-emerald-400" title="YouTube stats merged with internal production record">YT+</span>}
                  </td>
                  <td className="px-4 py-3 text-[var(--pv-text-2)]">{num(v.views)}</td>
                  <td className="px-4 py-3 text-[var(--pv-text-2)]">{money(v.revenue)}</td>
                  <td className="px-4 py-3 text-[var(--pv-text-2)]">{v.cost == null ? "—" : money(v.cost)}</td>
                  <td className={`px-4 py-3 font-bold ${v.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(v.profit)}</td>
                  <td className="px-4 py-3 text-[var(--pv-text-3)]">{v.revisions ?? "—"}</td>
                  <td className="max-w-[180px] truncate px-4 py-3 text-[var(--pv-text-4)]">{[v.animator, v.editor, v.scriptwriter].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-[var(--pv-text-4)]">{(v.status || "").replace("_", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ==================== TRACKERS & GOALS ==================== */
const TrackersTab = ({ summary, trackers, onChanged }) => {
  const [trackerForm, setTrackerForm] = useState({ name: "", type: "number", frequency: "daily" });
  const [goalForm, setGoalForm] = useState({ name: "", metric: "views", period: "monthly", target: "" });
  const [entryFor, setEntryFor] = useState(null);
  const [entry, setEntry] = useState({ date: today(), value: "" });
  const [saving, setSaving] = useState(false);
  const fmtVal = (t, v) => (t.type === "currency" ? money(v) : t.type === "percentage" ? `${v}%` : num(v));

  const createTracker = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/analytics/trackers`, trackerForm, { headers: authHeaders() });
      toast.success("Tracker created.");
      setTrackerForm({ name: "", type: "number", frequency: "daily" });
      onChanged();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not create tracker."); }
    finally { setSaving(false); }
  };

  const deleteTracker = async (id) => {
    try {
      await axios.delete(`${API}/admin/analytics/trackers/${id}`, { headers: authHeaders() });
      toast.success("Tracker deleted.");
      onChanged();
    } catch { toast.error("Could not delete tracker."); }
  };

  const addEntry = async (tid) => {
    if (entry.value === "") return toast.error("Enter a value.");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/analytics/trackers/${tid}/entries`, { date: entry.date, value: parseFloat(entry.value) }, { headers: authHeaders() });
      toast.success("Entry added.");
      setEntryFor(null);
      setEntry({ date: today(), value: "" });
      onChanged();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not add entry."); }
    finally { setSaving(false); }
  };

  const createGoal = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/analytics/goals`, { ...goalForm, target: parseFloat(goalForm.target) || 0 }, { headers: authHeaders() });
      toast.success("Goal created.");
      setGoalForm({ name: "", metric: "views", period: "monthly", target: "" });
      onChanged();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not create goal."); }
    finally { setSaving(false); }
  };

  const deleteGoal = async (id) => {
    try {
      await axios.delete(`${API}/admin/analytics/goals/${id}`, { headers: authHeaders() });
      toast.success("Goal deleted.");
      onChanged();
    } catch { toast.error("Could not delete goal."); }
  };

  return (
    <div data-testid="analytics-trackers" className="grid gap-6 lg:grid-cols-2">
      <div className={cardCls}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Custom trackers</p>
        <p className="mt-1 text-xs text-[var(--pv-text-4)]">Track anything — Discord members, sponsor deals, hours worked.</p>
        <form data-testid="tracker-form" onSubmit={createTracker} className="mt-4 flex flex-wrap items-end gap-2">
          <div className="min-w-40 flex-1"><label className={labelCls}>Name</label><input data-testid="tracker-name" required minLength={2} value={trackerForm.name} onChange={(e) => setTrackerForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Discord members" /></div>
          <div><label className={labelCls}>Type</label>
            <select data-testid="tracker-type" value={trackerForm.type} onChange={(e) => setTrackerForm((f) => ({ ...f, type: e.target.value }))} className={inputCls}>
              <option value="number">Number</option><option value="currency">Currency</option><option value="percentage">Percentage</option>
            </select>
          </div>
          <div><label className={labelCls}>Frequency</label>
            <select data-testid="tracker-frequency" value={trackerForm.frequency} onChange={(e) => setTrackerForm((f) => ({ ...f, frequency: e.target.value }))} className={inputCls}>
              <option value="daily">Daily</option><option value="per_video">Per video</option><option value="manual">Manual</option>
            </select>
          </div>
          <button data-testid="tracker-create-btn" type="submit" disabled={saving} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create
          </button>
        </form>

        <div className="mt-4 space-y-3">
          {trackers.length === 0 && <p data-testid="trackers-empty" className="text-sm text-[var(--pv-text-4)]">No trackers yet.</p>}
          {trackers.map((t) => {
            const latest = t.entries[t.entries.length - 1];
            return (
              <div key={t.id} data-testid={`tracker-card-${t.id}`} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-[var(--pv-text)]">{t.name}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{t.type} · {t.frequency.replace("_", " ")} · {t.entries.length} entries</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {latest && <span data-testid={`tracker-latest-${t.id}`} className="font-display text-lg font-bold text-cyan-400">{fmtVal(t, latest.value)}</span>}
                    <button data-testid={`tracker-add-entry-${t.id}`} onClick={() => { setEntryFor(entryFor === t.id ? null : t.id); setEntry({ date: today(), value: "" }); }} className="rounded-full border border-cyan-500/30 bg-cyan-500/10 p-1.5 text-cyan-300 hover:bg-cyan-500/20"><Plus className="h-3.5 w-3.5" /></button>
                    <button data-testid={`tracker-delete-${t.id}`} onClick={() => deleteTracker(t.id)} className="rounded-full p-1.5 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                {entryFor === t.id && (
                  <div data-testid={`tracker-entry-form-${t.id}`} className="mt-3 flex items-end gap-2">
                    <div><label className={labelCls}>Date</label><input data-testid={`tracker-entry-date-${t.id}`} type="date" max={today()} value={entry.date} onChange={(e) => setEntry((x) => ({ ...x, date: e.target.value }))} className={inputCls} /></div>
                    <div><label className={labelCls}>Value</label><input data-testid={`tracker-entry-value-${t.id}`} type="number" step="any" value={entry.value} onChange={(e) => setEntry((x) => ({ ...x, value: e.target.value }))} className={inputCls} /></div>
                    <button data-testid={`tracker-entry-save-${t.id}`} onClick={() => addEntry(t.id)} disabled={saving} className="rounded-full bg-cyan-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60">Add</button>
                  </div>
                )}
                {t.entries.length > 1 && (
                  <div className="mt-3" style={{ width: "100%", height: 80 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={t.entries.map((e) => ({ date: e.date.slice(5), value: e.value }))}>
                        <Line type="monotone" dataKey="value" stroke={CYAN} strokeWidth={2} dot={false} />
                        <XAxis dataKey="date" hide /><YAxis hide domain={["auto", "auto"]} />
                        <Tooltip contentStyle={tooltipStyle} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className={cardCls}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Goals</p>
        <p className="mt-1 text-xs text-[var(--pv-text-4)]">Progress resets weekly or monthly against your daily metrics and completions.</p>
        <form data-testid="goal-form" onSubmit={createGoal} className="mt-4 flex flex-wrap items-end gap-2">
          <div className="min-w-40 flex-1"><label className={labelCls}>Name</label><input data-testid="goal-name" required minLength={2} value={goalForm.name} onChange={(e) => setGoalForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="100k views this month" /></div>
          <div><label className={labelCls}>Metric</label>
            <select data-testid="goal-metric" value={goalForm.metric} onChange={(e) => setGoalForm((f) => ({ ...f, metric: e.target.value }))} className={inputCls}>
              <option value="views">Views</option><option value="revenue">Revenue</option><option value="videos_published">Videos published</option><option value="production_completed">Jobs completed</option>
            </select>
          </div>
          <div><label className={labelCls}>Period</label>
            <select data-testid="goal-period" value={goalForm.period} onChange={(e) => setGoalForm((f) => ({ ...f, period: e.target.value }))} className={inputCls}>
              <option value="weekly">Weekly</option><option value="monthly">Monthly</option>
            </select>
          </div>
          <div className="w-28"><label className={labelCls}>Target</label><input data-testid="goal-target" required type="number" min="1" value={goalForm.target} onChange={(e) => setGoalForm((f) => ({ ...f, target: e.target.value }))} className={inputCls} /></div>
          <button data-testid="goal-create-btn" type="submit" disabled={saving} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create
          </button>
        </form>

        <div className="mt-4 space-y-3">
          {summary.goals.length === 0 && <p data-testid="goals-empty" className="text-sm text-[var(--pv-text-4)]">No goals yet.</p>}
          {summary.goals.map((g) => (
            <div key={g.id} data-testid={`goal-row-${g.id}`} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-[var(--pv-text)]">{g.name}</p>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold ${g.pct >= 100 ? "text-emerald-400" : "text-cyan-400"}`}>{g.pct}%</span>
                  <button data-testid={`goal-delete-${g.id}`} onClick={() => deleteGoal(g.id)} className="rounded-full p-1.5 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--pv-raised)]">
                <div className={`h-full rounded-full ${g.pct >= 100 ? "bg-emerald-400" : "bg-cyan-500"}`} style={{ width: `${g.pct}%` }} />
              </div>
              <p className="mt-1.5 text-[11px] text-[var(--pv-text-4)]">{num(g.current)} / {num(g.target)} {g.metric.replace(/_/g, " ")} · {g.period} · {g.days_left} days left</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ==================== DATA (CSV import/export) ==================== */
const DataTab = ({ onChanged }) => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const download = async (name) => {
    try {
      const r = await axios.get(`${API}/admin/analytics/export/${name}.csv`, { headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const runImport = async (commit) => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a CSV file first.");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/analytics/import/daily?commit=${commit}`, form, { headers: authHeaders() });
      setPreview(data);
      if (commit) {
        toast.success(`Imported ${data.valid} day(s).`);
        setPreview(null);
        if (fileRef.current) fileRef.current.value = "";
        onChanged();
      }
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Import failed.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="analytics-data" className="grid gap-6 lg:grid-cols-2">
      <div className={cardCls}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Export CSV</p>
        <p className="mt-1 text-xs text-[var(--pv-text-4)]">Download your data anytime — it's yours.</p>
        <div className="mt-4 space-y-2">
          {[
            { id: "daily", label: "Daily metrics", desc: "date, views, revenue, videos, subs, income, notes" },
            { id: "videos", label: "Video library", desc: "titles, crew, views, revenue, cost, profit, revisions" },
            { id: "payments", label: "Payments", desc: "worker, job, amount, base, bonus, status, dates" },
          ].map((x) => (
            <button key={x.id} data-testid={`export-${x.id}-btn`} onClick={() => download(x.id)} className="flex w-full items-center justify-between rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-3 text-left transition-colors hover:border-cyan-500/30">
              <span>
                <span className="block text-sm font-bold text-[var(--pv-text)]">{x.label}</span>
                <span className="block text-[11px] text-[var(--pv-text-4)]">{x.desc}</span>
              </span>
              <Download className="h-4 w-4 shrink-0 text-cyan-400" />
            </button>
          ))}
        </div>
      </div>

      <div className={cardCls}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Import daily metrics (CSV)</p>
        <p className="mt-1 text-xs text-[var(--pv-text-4)]">Bring your history from Notion or YouTube Studio. Columns: <span className="font-mono-label text-[10px]">date, views, revenue, videos_published, subs_gained, business_income, notes</span>. Dates already in the system are skipped.</p>
        <input data-testid="import-file-input" ref={fileRef} type="file" accept=".csv,text/csv" className="mt-4 block w-full text-xs text-[var(--pv-text-3)] file:mr-3 file:rounded-full file:border-0 file:bg-cyan-500 file:px-4 file:py-2 file:text-xs file:font-bold file:text-white hover:file:bg-cyan-600" />
        <div className="mt-3 flex gap-2">
          <button data-testid="import-preview-btn" onClick={() => runImport(false)} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:border-cyan-500/30 disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Preview
          </button>
          {preview && preview.valid > 0 && (
            <button data-testid="import-commit-btn" onClick={() => runImport(true)} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60">
              Import {preview.valid} row(s)
            </button>
          )}
        </div>
        {preview && (
          <div data-testid="import-preview" className="mt-4 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4 text-xs">
            <p className="font-bold text-[var(--pv-text)]">{preview.valid} valid · {preview.errors.length} errors · {preview.duplicates.length} duplicates (skipped)</p>
            {preview.errors.slice(0, 5).map((e, i) => <p key={i} className="mt-1 text-rose-300">Row {e.row}: {e.error}</p>)}
            {preview.duplicates.slice(0, 5).map((d, i) => <p key={i} className="mt-1 text-amber-300">Row {d.row}: {d.date} already exists</p>)}
            {preview.preview.slice(0, 5).map((r, i) => <p key={i} className="mt-1 text-[var(--pv-text-3)]">{r.date} — {num(r.views)} views, {money(r.revenue)}</p>)}
          </div>
        )}
      </div>
    </div>
  );
};
